import { useState } from 'react';
import {
  useCreateProductOption,
  useMenuTree,
  useUpdateProduct,
} from '@/features/menu/hooks/useMenu';
import { normalizeProductCode } from '@restaurant/types';
import { loc } from '@/utils/localized';
import { LABELS, OPTIONAL_GROUP_NAME } from '@/features/menu/constants';
import { pretty, toLocalizedRecord } from '@/features/menu/utils/product-helpers';
import { fail, router } from '@/features/menu/utils/product-page-actions';
import AllergenCodeSelect from '@/features/menu/components/AllergenCodeSelect';
import CodePopoverSelect from '@/features/menu/components/CodePopoverSelect';
import LocalizedTextSheet from '@/features/menu/components/LocalizedTextSheet';
import PricingSection from '@/features/menu/components/PricingSection';
import ProductPhotoBlock from '@/features/menu/components/ProductPhotoBlock';
import { useI18nStore, useT } from '@/i18n';
import { useAllergenTable } from '@/features/allergen-labeling/hooks/useAllergenLabeling';
import { useRestaurantStore } from '@/stores/restaurant.store';
import ScheduleEditor from '@/features/menu/components/ScheduleEditor';
import type { CategorySchedule, Product, ProductUpdate } from '@restaurant/api-contracts';
import { ProductLabel } from '@restaurant/types';
import {
  BlockFooter,
  BlockTitle,
  f7,
  Link as F7Link,
  List,
  ListItem,
  Popover,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { ChangePulseMarker, ProposedValue } from '@/features/ai-changeset/components/ProposedValue';
import type { NavigationStore } from '@/features/ai-changeset/hooks/useChangeNavigation';
import {
  changedRowClass,
  runPositions,
  type RunPosition,
} from '@/features/ai-changeset/utils/changeset-view';
import { optionPickLabel } from '@/features/menu/utils/option-limits-text';
import { ProductOptionRow } from '@/features/menu/components/ProductOptionRow';
import {
  productFieldTarget,
  type Draft,
  type ProductField,
  type ProductView,
} from '@/features/menu/utils/menu-draft';
import { describeHours } from '@/features/menu/utils/menu-draft-text';

type ProductDetail = Product;

type EditTarget = 'name' | 'description';

export default function ProductDetailView({
  product,
  menuId,
  view,
  store,
}: {
  product: ProductDetail;
  menuId: string;
  view: ProductView;
  store: NavigationStore;
}) {
  const t = useT();
  const q = t.menuQuickEdit;
  const locale = useI18nStore((s) => s.locale);
  const updateProduct = useUpdateProduct();
  const createOption = useCreateProductOption();
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const { data: tree } = useMenuTree(menuId);
  const categories = (tree?.categories ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);
  const currentCategory = categories.find((c) => c.id === product.categoryId);

  const [editing, setEditing] = useState<EditTarget | null>(null);
  const openEdit = (target: EditTarget) => setEditing(target);
  const closeEdit = () => setEditing(null);

  const editCode = () => {
    f7.dialog.prompt(
      t.menu.productForm.code,
      t.menu.productForm.editCode,
      (value) => {
        const code = normalizeProductCode(value);
        patch({ code });
      },
      undefined,
      product.code ?? '',
    );
  };

  const allergenTable = useAllergenTable();
  const allergenCodes = product.allergenCodes;
  const labels = product.labels.filter((l): l is ProductLabel => (LABELS as string[]).includes(l));

  const patch = (body: ProductUpdate) =>
    updateProduct.mutate({ productId: product.id, body }, { onError: fail });

  const addOption = () => {
    if (createOption.isPending) return;
    createOption.mutate(
      {
        productId: product.id,
        body: {
          name: OPTIONAL_GROUP_NAME,
          isRequired: false,
          isMultiple: false,
          minSelect: 0,
          maxSelect: 1,
        },
      },
      {
        onSuccess: (created) =>
          router()?.navigate(`/menu/${menuId}/product/${product.id}/option/${created.id}`),
        onError: fail,
      },
    );
  };

  const target = (field: ProductField) => productFieldTarget(product.id, field);
  const marked = (field: ProductField, run: RunPosition | null, className?: string) => ({
    className: [className, changedRowClass(run)].filter(Boolean).join(' ') || undefined,
    'data-quick-edit-target': run ? target(field) : undefined,
    'data-testid': `product-${field}-row`,
  });
  const pulse = (field: ProductField, on: boolean) =>
    on && (
      <ChangePulseMarker
        slot="root-start"
        store={store}
        id={target(field)}
        testId={`product-${field}-pulse`}
      />
    );
  const draftRow = (field: ProductField, title: string | null, draft: Draft<string> | undefined) =>
    draft && (
      <List strongIos insetIos dividersIos>
        <ListItem title={title ?? undefined} {...marked(field, 'single')}>
          {pulse(field, true)}
          <ProposedValue
            slot={title ? 'footer' : 'title'}
            start
            before={draft.before}
            after={draft.after}
          />
        </ListItem>
      </List>
    );
  const listed = (values: string[]) => values.map(pretty).join(', ') || q.none;
  const flag = (draft: Draft<boolean> | undefined, on: string, off: string) =>
    draft && { before: draft.before ? on : off, after: draft.after ? on : off };
  const [nameRun, descriptionRun] = runPositions([!!view.name, !!view.description, false]);
  const [activeRun, inStockRun] = runPositions([!!view.active, !!view.inStock]);
  const activeDraft = flag(view.active, q.shown, q.hidden);
  const inStockDraft = flag(view.inStock, q.inStock, q.soldOut);
  const optionRuns = runPositions(view.options.map((o) => o.changed));

  const openCategoryPicker = (e: React.MouseEvent) => {
    if (categories.length === 0) return;
    f7.popover.open('.product-category-popover', e.currentTarget as HTMLElement);
  };

  const pickCategory = (categoryId: string) => {
    f7.popover.close('.product-category-popover');
    if (categoryId !== product.categoryId) patch({ categoryId });
  };

  return (
    <>
      <BlockTitle>{t.menu.productForm.general}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem
          title={t.menu.productForm.code}
          after={product.code ?? t.menu.productForm.noCode}
          link="#"
          onClick={editCode}
        />
        <ListItem
          title={t.menu.productForm.name}
          after={view.name ? undefined : loc(product.name)}
          link="#"
          onClick={() => openEdit('name')}
          {...marked('name', nameRun ?? null)}
        >
          {pulse('name', !!view.name)}
          {view.name && (
            <ProposedValue slot="footer" start before={view.name.before} after={view.name.after} />
          )}
        </ListItem>
        <ListItem
          title={t.menu.productForm.description}
          after={view.description ? undefined : loc(product.description) || t.menu.noDescription}
          link="#"
          onClick={() => openEdit('description')}
          {...marked('description', descriptionRun ?? null, 'item-after-ellipsis')}
        >
          {pulse('description', !!view.description)}
          {view.description && (
            <ProposedValue
              slot="footer"
              start
              before={view.description.before}
              after={view.description.after}
            />
          )}
        </ListItem>
        <ListItem
          className="item-after-ellipsis"
          title={t.menu.category}
          after={currentCategory ? loc(currentCategory.name) : ''}
          link="#"
          onClick={(e: React.MouseEvent) => openCategoryPicker(e)}
        />
      </List>
      <Popover className="product-category-popover">
        <List strongIos dividersIos className="category-picker-list">
          {categories.map((c) => (
            <ListItem
              key={c.id}
              title={loc(c.name)}
              link="#"
              noChevron
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                pickCategory(c.id);
              }}
            >
              {c.id === product.categoryId && (
                <i
                  slot="after"
                  className="f7-icons"
                  style={{ color: 'var(--f7-theme-color)', fontSize: 22 }}
                >
                  checkmark
                </i>
              )}
            </ListItem>
          ))}
        </List>
      </Popover>

      <PricingSection product={product} menuId={menuId} view={view} store={store} />

      <BlockTitle>{t.menu.productForm.options}</BlockTitle>
      {view.options.length > 0 && (
        <List
          strongIos
          strongMd
          insetIos
          insetMd
          dividersIos
          dividersMd
          // Framework7 clamps .item-text to --f7-list-item-text-max-lines (2) via
          // line-clamp + max-height, which hid a 3rd+ choice. Lift the cap so the
          // full choices list under each option shows.
          style={{ '--f7-list-item-text-max-lines': 99 } as React.CSSProperties}
        >
          {view.options.map((option, i) =>
            ProductOptionRow({
              view: option,
              run: optionRuns[i] ?? null,
              store,
              currency,
              pickLabel: optionPickLabel(option, t.menu.optionLimits),
              onOpen:
                option.status === 'new'
                  ? null
                  : () =>
                      router()?.navigate(
                        `/menu/${menuId}/product/${product.id}/option/${option.option.id}`,
                      ),
            }),
          )}
        </List>
      )}
      {/* Centered "+ Add option" control; when there are none it also serves as the
          empty state (footer note below explains). Strip the label's leading "+ "
          so it doesn't double up with the green plus circle. */}
      <div className="bulk-add-card">
        <div
          className="bulk-add-addrow-btn bulk-add-addrow-row"
          role="button"
          tabIndex={0}
          aria-disabled={createOption.isPending}
          style={createOption.isPending ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
          onClick={addOption}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && addOption()}
        >
          <button
            type="button"
            className="bulk-add-plus"
            aria-label={t.menu.addOption}
            tabIndex={-1}
          />
          <span>{t.menu.addOption.replace(/^\+\s*/, '')}</span>
        </div>
      </div>
      {view.options.length === 0 && <BlockFooter>{t.menu.noOptionsYet}</BlockFooter>}

      <BlockTitle>{t.menu.productForm.availability}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem
          title={t.menu.productForm.visibleToCustomers}
          footer={t.menu.productForm.hiddenProductsFooter}
          {...marked('active', activeRun ?? null)}
        >
          {pulse('active', !!activeDraft)}
          {activeDraft ? (
            <ProposedValue slot="after" before={activeDraft.before} after={activeDraft.after} />
          ) : (
            <ToggleSwitch
              slot="after"
              checked={product.isActive}
              onChange={(v) => patch({ isActive: v })}
            />
          )}
        </ListItem>
        <ListItem
          title={t.menu.productForm.inStock}
          footer={t.menu.productForm.outOfStockProductsFooter}
          {...marked('inStock', inStockRun ?? null)}
        >
          {pulse('inStock', !!inStockDraft)}
          {inStockDraft ? (
            <ProposedValue slot="after" before={inStockDraft.before} after={inStockDraft.after} />
          ) : (
            <ToggleSwitch
              slot="after"
              checked={product.inStock}
              onChange={(v) => patch({ inStock: v })}
            />
          )}
        </ListItem>
      </List>

      {draftRow(
        'hours',
        q.hours,
        view.hours && {
          before: describeHours(view.hours.before, locale, q),
          after: describeHours(view.hours.after, locale, q),
        },
      )}

      <ScheduleEditor
        key={product.id}
        value={(product.schedule ?? null) as CategorySchedule | null}
        onChange={(s) => patch({ schedule: s })}
      />

      <BlockTitle>{t.menu.productForm.allergens}</BlockTitle>
      {draftRow(
        'codes',
        null,
        view.codes && { before: listed(view.codes.before), after: listed(view.codes.after) },
      )}
      {(allergenTable.data?.length ?? 0) === 0 ? (
        <BlockFooter>
          {t.menu.productForm.noAllergenTable}{' '}
          <F7Link href="/menu/allergen-labeling">{t.menu.productForm.setUpAllergenTable}</F7Link>
        </BlockFooter>
      ) : (
        <AllergenCodeSelect
          table={allergenTable.data!}
          selected={allergenCodes}
          editHref={`/menu/${menuId}/product/${product.id}/allergen-codes`}
          editLabel={t.menu.productForm.editAllergenCodes}
        />
      )}

      <BlockTitle>{t.menu.productForm.labels}</BlockTitle>
      {draftRow(
        'labels',
        null,
        view.labels && { before: listed(view.labels.before), after: listed(view.labels.after) },
      )}
      <CodePopoverSelect
        title={t.menu.productForm.labels}
        all={LABELS}
        selected={labels}
        onChange={(codes) => patch({ labels: codes })}
        popoverClass="product-labels-popover"
      />

      <ProductPhotoBlock product={product} />

      <LocalizedTextSheet
        open={editing === 'name'}
        onClose={closeEdit}
        title={t.menu.productForm.editName}
        fieldLabel={t.menu.productForm.name}
        required
        initial={toLocalizedRecord(product.name)}
        onSubmit={(v, done, err) => {
          if (!v) return err(new Error(t.menu.englishValueRequired));
          updateProduct.mutate(
            { productId: product.id, body: { name: v } },
            { onSuccess: done, onError: err },
          );
        }}
      />
      <LocalizedTextSheet
        open={editing === 'description'}
        onClose={closeEdit}
        title={t.menu.productForm.editDescription}
        fieldLabel={t.menu.productForm.description}
        textarea
        initial={toLocalizedRecord(product.description)}
        onSubmit={(v, done, err) => {
          updateProduct.mutate(
            { productId: product.id, body: { description: v ?? null } },
            { onSuccess: done, onError: err },
          );
        }}
      />
    </>
  );
}
